// ─────────────────────────────────────────────────────────────────────────────
// Price A/B experiment framework (color analysis) — server-safe config.
//
// One place to define the live experiment. A visitor is assigned to a bucket
// ONCE and it sticks (localStorage), so nobody ever sees one price today and a
// different price tomorrow — that inconsistency is a trust/refund risk, which
// is exactly what these tests are trying to avoid.
//
// Every bucket carries an explicit `priceCents` — the amount that will actually
// be charged. For presentation-only tests (like the old anchor test) every
// bucket charges the same; only `anchor`/`showGuarantee` differ. When you move
// to a real price test, change `priceCents` per bucket here and the server
// routes will charge it automatically (they read the bucket, not the display).
//
// Judge winners by REVENUE PER VISITOR (conversion × price), not conversion
// alone. Every funnel event is tagged with the bucket + priceCents so Gizmo can
// compute RPV per arm (breakdown_by_property on `bucket`).
// ─────────────────────────────────────────────────────────────────────────────

export type PriceBucket = {
  /** Stable id, sent on every event. Keep short + stable for Gizmo breakdowns. */
  id: string;
  /** USD amount actually charged, in cents. Server reads this to set unit_amount. */
  priceCents: number;
  /** Struck-through anchor to display above the price, or null for none. */
  anchor: number | null;
  /** Whether to show the money-back guarantee line for this arm. */
  showGuarantee: boolean;
  /**
   * Optional honest-scarcity line shown under the CTA (e.g. "Introductory
   * price"). Keep it TRUTHFUL — no fake deadlines, no countdown timers.
   * i18n key resolved by the display component, or null.
   */
  scarcityKey: string | null;
  /** Relative sampling weight (need not sum to anything; normalized at runtime). */
  weight: number;
};

export type PriceExperiment = {
  /** Bump this key whenever buckets change so old assignments are discarded. */
  key: string;
  buckets: PriceBucket[];
};

// ── SETTLED PRICE ────────────────────────────────────────────────────────────
// ca-price-reverted-v1 ($9.99/$19.99/$24.99/$29.99) concluded 2026-07-17:
// Mike settled on $24.99 as the western sticker price. One bucket, weight 1 —
// the framework stays in place so the next test is a config change away. The
// key bump discards old sticky assignments; retired arms are honored via
// LEGACY_BUCKETS for in-flight bundles.
//
// No anchor: a struck-through $29.99 forever would be a fake permanent sale
// (site-wide fake-proof rule). Regional visitors still get PPP pricing —
// constants/pricing.ts scales this priceCents per country and enforces the
// HARD BUSINESS RULE (Mike, 2026-07-10): nothing is ever sold for less than
// $5 USD equivalent, anywhere in the world.
export const PRICE_EXPERIMENT: PriceExperiment = {
  key: "ca-price-settled-2499",
  buckets: [
    { id: "p2499", priceCents: 2499, anchor: null, showGuarantee: true, scarcityKey: null, weight: 1 },
  ],
};

// Arms from retired experiments that an in-flight client (bundle loaded just
// before a deploy) may still send at checkout. Resolving these at the price
// they DISPLAYED — instead of falling back to the $29.99 control — guarantees
// nobody is ever charged more than the number they were shown. Never
// assignable (not in PRICE_EXPERIMENT.buckets); prune entries once the deploy
// they bridge is a few days old.
const LEGACY_BUCKETS: PriceBucket[] = [
  // ca-price-reverted-v1 arms retired 2026-07-17 when the price settled at
  // $24.99 (p2499 lives on in PRICE_EXPERIMENT). Prune after a few days.
  { id: "p999", priceCents: 999, anchor: null, showGuarantee: true, scarcityKey: null, weight: 0 },
  { id: "p1999", priceCents: 1999, anchor: null, showGuarantee: true, scarcityKey: null, weight: 0 },
  { id: "p2999", priceCents: 2999, anchor: null, showGuarantee: true, scarcityKey: null, weight: 0 },
];

/**
 * Server-side bucket lookup. Client sends only the bucket ID; the amount
 * charged always comes from this config, never from the client. Retired ids
 * from the previous experiment resolve at their displayed price (see
 * LEGACY_BUCKETS); unknown or missing ids (tampering, ancient bundles) fall
 * back to the control bucket.
 */
export const resolveBucket = (id: unknown): PriceBucket => {
  const control = PRICE_EXPERIMENT.buckets[PRICE_EXPERIMENT.buckets.length - 1];
  if (typeof id !== "string") return control;
  return (
    PRICE_EXPERIMENT.buckets.find((b) => b.id === id) ??
    LEGACY_BUCKETS.find((b) => b.id === id) ??
    control
  );
};

/**
 * Standard event metadata for RPV analysis. Spread into every checkout_start /
 * purchase event so Gizmo can break down by `bucket` and sum revenue.
 */
export const bucketEventMeta = (bucket: PriceBucket) => ({
  experiment: PRICE_EXPERIMENT.key,
  bucket: bucket.id,
  price_cents: bucket.priceCents,
});
